// Sugalarm — Siri App-Group bridge diagnostic (cycle 249).
//
// A self-contained, READ-MOSTLY probe that runs the entire Siri auth seam
// live on the device and shows where it breaks, so "Open Sugalarm and sign
// in first" while signed in can be localized without talking to Siri:
//
//   native runtime?  →  AuthBridge plugin registered?  →  webview JWT present?
//   →  write the JWT into the App Group (the real writer path, awaited)
//   →  read it back out of the App Group
//
// The write step uses the SAME shape the cycle-41 writer uses (proper `exp`
// / `sub` decoded from the JWT — never clobbers the container with garbage),
// so a successful run also POPULATES the container as a side effect: if the
// chain is healthy, running this can itself fix the intents.
//
// Renders nothing off the Capacitor native runtime (web / kiosk / PWA), so
// it only ever appears inside the iOS/Android app. Reached through
// `window.Capacitor.Plugins` (cycle 26/138 rule), never a bare import.

import { useCallback, useEffect, useState } from "react";

import { getJwt } from "../../lib/auth";
import { capacitorPlatform, isCapacitorNative } from "../../lib/capacitor";

interface BridgeProbe {
  setTokens?: (p: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
    userId: string;
  }) => Promise<void>;
  getTokens?: () => Promise<{ accessToken?: string; refreshToken?: string }>;
}

function authBridge(): BridgeProbe | null {
  const cap = (
    window as { Capacitor?: { Plugins?: { AuthBridge?: BridgeProbe } } }
  ).Capacitor;
  return cap?.Plugins?.AuthBridge ?? null;
}

interface AppInfoPlugin {
  getInfo?: () => Promise<{ version?: string; build?: string }>;
}

/** The @capacitor/app pod plugin IS registered (CocoaPods framework plugin),
 *  unlike the app-target AuthBridge — so its getInfo() returns the installed
 *  native CFBundleShortVersionString/CFBundleVersion, ending the guessing
 *  about which iOS shell archive is actually on the phone. */
function appInfoPlugin(): AppInfoPlugin | null {
  const cap = (
    window as { Capacitor?: { Plugins?: { App?: AppInfoPlugin } } }
  ).Capacitor;
  return cap?.Plugins?.App ?? null;
}

/** Decode `exp` (number) + `sub` (string) from a JWT without verifying —
 *  exactly what the cycle-41 writer mirrors, so the probe write doesn't
 *  overwrite the container's real claims with placeholders. */
function decodeExpSub(token: string): { exp: number; sub: string } {
  try {
    const part = token.split(".")[1];
    if (!part) return { exp: 0, sub: "" };
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const p = JSON.parse(atob(padded)) as { exp?: unknown; sub?: unknown };
    return {
      exp: typeof p.exp === "number" ? p.exp : 0,
      sub: typeof p.sub === "string" ? p.sub : "",
    };
  } catch {
    return { exp: 0, sub: "" };
  }
}

/** Every natively-registered plugin name, the DECISIVE registration readout.
 *
 *  Ground truth is `window.Capacitor.PluginHeaders` — the list iOS injects of
 *  every plugin actually registered on the native bridge; `@capacitor/core`
 *  builds the `window.Capacitor.Plugins.X` proxies from it (which is why the
 *  SPA reaches PushNotifications/App/Haptics with no `@capacitor/*` JS dep).
 *  So: if `AuthBridge` is in this list it IS registered natively (and the
 *  bridge lookup above is the bug); if it's absent native registration
 *  genuinely failed. Pod-framework plugins register regardless, so seeing
 *  them but not the app-target custom plugins (AuthBridge / LiveActivityBridge
 *  / NotificationSettings) localizes the failure to app-target registration —
 *  and if the legacy-`.m` ones (LiveActivityBridge / NotificationSettings)
 *  appear but AuthBridge doesn't, the cycle-251 conversion is the regression.
 *
 *  Unions PluginHeaders names with `Object.keys(Plugins)` so nothing is missed
 *  regardless of how the runtime populates the proxy object. */
function registeredPlugins(): string[] {
  const cap = (
    window as {
      Capacitor?: {
        Plugins?: Record<string, unknown>;
        PluginHeaders?: Array<{ name?: string }>;
      };
    }
  ).Capacitor;
  const names = new Set<string>();
  for (const h of cap?.PluginHeaders ?? []) {
    if (typeof h?.name === "string") names.add(h.name);
  }
  if (cap?.Plugins) {
    for (const k of Object.keys(cap.Plugins)) names.add(k);
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

interface Probe {
  platform: string;
  bridgePresent: boolean;
  getTokensPresent: boolean;
  jwtPresent: boolean;
  readBefore: boolean | null;
  wrote: boolean | null;
  readAfter: boolean | null;
  appVersion: string | null;
  appBuild: string | null;
  plugins: string[];
  error: string | null;
}

function mark(v: boolean | null): string {
  if (v === null) return "—";
  return v ? "✓ yes" : "✗ no";
}
function color(v: boolean | null): string {
  if (v === null) return "var(--ink-3)";
  return v ? "var(--zone-ok)" : "var(--zone-urgent)";
}

export function SiriBridgeDiagnostics() {
  const native = isCapacitorNative();
  const [p, setP] = useState<Probe | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    const b = authBridge();
    const jwt = getJwt();
    const res: Probe = {
      platform: capacitorPlatform(),
      bridgePresent: b != null,
      getTokensPresent: typeof b?.getTokens === "function",
      jwtPresent: jwt != null,
      readBefore: null,
      wrote: null,
      readAfter: null,
      appVersion: null,
      appBuild: null,
      plugins: registeredPlugins(),
      error: null,
    };
    try {
      const app = appInfoPlugin();
      if (typeof app?.getInfo === "function") {
        const info = await app.getInfo();
        res.appVersion = info?.version ?? null;
        res.appBuild = info?.build ?? null;
      }
      if (typeof b?.getTokens === "function") {
        const t = await b.getTokens();
        res.readBefore = !!(t?.accessToken && t?.refreshToken);
      }
      if (typeof b?.setTokens === "function" && jwt) {
        const { exp, sub } = decodeExpSub(jwt.access);
        await b.setTokens({
          accessToken: jwt.access,
          refreshToken: jwt.refresh,
          expiresAt: exp,
          userId: sub,
        });
        res.wrote = true;
      }
      if (typeof b?.getTokens === "function") {
        const t = await b.getTokens();
        res.readAfter = !!(t?.accessToken && t?.refreshToken);
      }
    } catch (e) {
      res.error = e instanceof Error ? e.message : String(e);
    }
    setP(res);
    setRunning(false);
  }, []);

  useEffect(() => {
    if (native) void run();
  }, [native, run]);

  // Off-native (web / kiosk / PWA) this surface does not exist.
  if (!native) return null;

  const rows: { label: string; value: string; c: string }[] = p
    ? [
        { label: "Native runtime", value: `✓ ${p.platform}`, c: "var(--zone-ok)" },
        {
          label: "App build",
          value:
            p.appVersion || p.appBuild
              ? `${p.appVersion ?? "?"} (${p.appBuild ?? "?"})`
              : "— unknown",
          c: p.appVersion || p.appBuild ? "var(--ink-2)" : "var(--ink-3)",
        },
        {
          label: "AuthBridge plugin",
          value: p.bridgePresent ? "✓ registered" : "✗ MISSING",
          c: color(p.bridgePresent),
        },
        {
          label: "Session token (JWT)",
          value: mark(p.jwtPresent),
          c: color(p.jwtPresent),
        },
        {
          label: "App Group — before write",
          value: mark(p.readBefore),
          c: color(p.readBefore),
        },
        { label: "Wrote token to container", value: mark(p.wrote), c: color(p.wrote) },
        {
          label: "App Group — after write",
          value: mark(p.readAfter),
          c: color(p.readAfter),
        },
      ]
    : [];

  const verdict = !p
    ? "Running…"
    : !p.bridgePresent
      ? "AuthBridge plugin is NOT registered on this build — the Siri seam can't be written. Needs a fresh native build."
      : !p.jwtPresent
        ? "No JWT in the webview — sign in, then run again."
        : p.readAfter
          ? "Write + read-back succeeded — the container now holds your session. Ask Siri again."
          : p.wrote
            ? "Wrote to the container but read it straight back empty — the native store isn't persisting."
            : "Couldn't write — see the error below.";

  return (
    <div
      style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        border: "0.5px solid var(--line-strong)",
        background: "var(--bg-elev)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
          Siri bridge diagnostic
        </span>
      </div>
      <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", marginBottom: 12 }}>
        Checks the seam the Siri intents read your sign-in from. PII-free.
      </div>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            padding: "6px 0",
            borderBottom: "0.5px solid var(--line)",
            fontSize: 14,
          }}
        >
          <span style={{ color: "var(--ink-2)" }}>{r.label}</span>
          <span style={{ color: r.c, fontWeight: 600, whiteSpace: "nowrap" }}>
            {r.value}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: 12,
          fontSize: 13,
          color: p?.readAfter ? "var(--zone-ok)" : "var(--ink)",
          lineHeight: 1.4,
        }}
      >
        {verdict}
      </div>
      {p?.error ? (
        <div style={{ marginTop: 8, fontSize: "var(--type-floor)", color: "var(--zone-urgent)" }}>
          Error: {p.error}
        </div>
      ) : null}
      {p ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", marginBottom: 4 }}>
            Registered native plugins ({p.plugins.length})
          </div>
          <div
            style={{
              fontSize: "var(--type-floor)",
              color: "var(--ink-2)",
              fontFamily: "monospace",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {p.plugins.length ? p.plugins.join(", ") : "— none"}
          </div>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => void run()}
        disabled={running}
        style={{
          marginTop: 14,
          minHeight: 44,
          width: "100%",
          borderRadius: 12,
          border: "1px solid var(--line-strong)",
          background: "var(--bg-sunk)",
          color: "var(--ink)",
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {running ? "Running…" : "Run again"}
      </button>
    </div>
  );
}
